<template>
  <div class="feedback-widget" v-if="visible">
    <hr class="feedback-hr" />
    <!-- Already submitted state -->
    <div v-if="existingFeedback" class="feedback-submitted">
      <span class="feedback-icon" :class="existingFeedback.reaction === 'up' ? 'thumb-up' : 'thumb-down'">
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path
            d="M7 22V11L2 11V22H7ZM7 11L11 2C11.93 2 12.82 2.37 13.47 3.03C14.13 3.68 14.5 4.57 14.5 5.5V9H20.04C20.3 8.99 20.56 9.04 20.8 9.15C21.04 9.25 21.25 9.41 21.42 9.61C21.59 9.8 21.71 10.03 21.77 10.28C21.83 10.53 21.83 10.79 21.77 11.04L19.77 20.04C19.67 20.46 19.42 20.83 19.08 21.1C18.73 21.36 18.31 21.51 17.87 21.5H7"
          />
        </svg>
      </span>
      <span class="feedback-thanks">Thanks for the feedback</span>
    </div>

    <!-- Thank you state (just submitted) -->
    <div v-else-if="submitted" class="feedback-submitted">
      <span class="feedback-icon" :class="selectedReaction === 'up' ? 'thumb-up' : 'thumb-down'">
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path
            d="M7 22V11L2 11V22H7ZM7 11L11 2C11.93 2 12.82 2.37 13.47 3.03C14.13 3.68 14.5 4.57 14.5 5.5V9H20.04C20.3 8.99 20.56 9.04 20.8 9.15C21.04 9.25 21.25 9.41 21.42 9.61C21.59 9.8 21.71 10.03 21.77 10.28C21.83 10.53 21.83 10.79 21.77 11.04L19.77 20.04C19.67 20.46 19.42 20.83 19.08 21.1C18.73 21.36 18.31 21.51 17.87 21.5H7"
          />
        </svg>
      </span>
      <span class="feedback-thanks">Thanks — that helps a lot.</span>
    </div>

    <!-- Expanded state (reaction selected, optional message) -->
    <div v-else-if="selectedReaction" class="feedback-expanded">
      <div class="feedback-prompt">
        <span class="feedback-icon" :class="selectedReaction === 'up' ? 'thumb-up' : 'thumb-down'">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              d="M7 22V11L2 11V22H7ZM7 11L11 2C11.93 2 12.82 2.37 13.47 3.03C14.13 3.68 14.5 4.57 14.5 5.5V9H20.04C20.3 8.99 20.56 9.04 20.8 9.15C21.04 9.25 21.25 9.41 21.42 9.61C21.59 9.8 21.71 10.03 21.77 10.28C21.83 10.53 21.83 10.79 21.77 11.04L19.77 20.04C19.67 20.46 19.42 20.83 19.08 21.1C18.73 21.36 18.31 21.51 17.87 21.5H7"
            />
          </svg>
        </span>
        <span>{{
          selectedReaction === 'up' ? 'Great! Can you share more?' : 'Sorry to hear that! Can you tell us more?'
        }}</span>
      </div>
      <textarea
        v-model="message"
        class="feedback-textarea"
        placeholder="Optional — tell us more..."
        rows="3"
        maxlength="1000"
        @keydown.meta.enter="submit()"
        @keydown.ctrl.enter="submit()"
      ></textarea>
      <div class="feedback-actions">
        <button class="feedback-submit" @click="submit()" :disabled="submitting">
          {{ submitting ? 'Sending...' : 'Send' }}
        </button>
      </div>
    </div>

    <!-- Default state -->
    <div v-else class="feedback-default">
      <span class="feedback-question">Was this helpful?</span>
      <div class="feedback-buttons">
        <button class="feedback-btn" @click="selectReaction('up')" title="Yes, helpful">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              d="M7 22V11L2 11V22H7ZM7 11L11 2C11.93 2 12.82 2.37 13.47 3.03C14.13 3.68 14.5 4.57 14.5 5.5V9H20.04C20.3 8.99 20.56 9.04 20.8 9.15C21.04 9.25 21.25 9.41 21.42 9.61C21.59 9.8 21.71 10.03 21.77 10.28C21.83 10.53 21.83 10.79 21.77 11.04L19.77 20.04C19.67 20.46 19.42 20.83 19.08 21.1C18.73 21.36 18.31 21.51 17.87 21.5H7"
            />
          </svg>
        </button>
        <button class="feedback-btn thumb-down-btn" @click="selectReaction('down')" title="Not helpful">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              d="M7 22V11L2 11V22H7ZM7 11L11 2C11.93 2 12.82 2.37 13.47 3.03C14.13 3.68 14.5 4.57 14.5 5.5V9H20.04C20.3 8.99 20.56 9.04 20.8 9.15C21.04 9.25 21.25 9.41 21.42 9.61C21.59 9.8 21.71 10.03 21.77 10.28C21.83 10.53 21.83 10.79 21.77 11.04L19.77 20.04C19.67 20.46 19.42 20.83 19.08 21.1C18.73 21.36 18.31 21.51 17.87 21.5H7"
            />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { submitFeedback, getProjectFeedback, type FeedbackItem } from '../services/api';

const props = defineProps<{
  projectId: string;
  frameworkId: string;
  outputName: string;
}>();

const selectedReaction = ref<'up' | 'down' | null>(null);
const message = ref('');
const submitted = ref(false);
const submitting = ref(false);
const existingFeedback = ref<FeedbackItem | null>(null);
const feedbackLoaded = ref(false);

const visible = computed(() => feedbackLoaded.value);

onMounted(async () => {
  try {
    const { feedback } = await getProjectFeedback(props.projectId);
    const match = feedback.find(f => f.frameworkId === props.frameworkId && f.outputName === props.outputName);
    if (match) {
      existingFeedback.value = match;
    }
  } catch {
    // Silently fail
  }
  feedbackLoaded.value = true;
});

function selectReaction(reaction: 'up' | 'down') {
  selectedReaction.value = reaction;
}

async function submit() {
  if (!selectedReaction.value || submitting.value) return;
  submitting.value = true;
  try {
    await submitFeedback(props.projectId, {
      frameworkId: props.frameworkId,
      outputName: props.outputName,
      reaction: selectedReaction.value,
      message: message.value.trim() || undefined,
    });
    submitted.value = true;
  } catch {
    submitted.value = true;
  } finally {
    submitting.value = false;
  }
}
</script>

<style scoped>
.feedback-widget {
  padding: 0 72px 48px;
  padding-top: 16px;
}

.feedback-hr {
  border: none;
  border-top: 1px solid #e0e0e0;
  margin: 0 0 16px 0;
}

@media (max-width: 1040px) {
  .feedback-widget {
    padding: 0 48px 48px;
    padding-top: 24px;
  }
}

@media (max-width: 768px) {
  .feedback-widget {
    padding: 0 24px 48px;
    padding-top: 24px;
  }
}

@media (max-width: 480px) {
  .feedback-widget {
    padding: 0 16px 48px;
    padding-top: 24px;
  }
}

@media print {
  .feedback-widget {
    display: none;
  }
}

.feedback-default,
.feedback-submitted {
  display: flex;
  align-items: center;
  gap: 10px;
}

.feedback-question {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: var(--color-text-muted);
}

.feedback-buttons {
  display: flex;
  gap: 6px;
}

.feedback-btn {
  background: none;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  padding: 6px 10px;
  cursor: pointer;
  transition: var(--transition-fast);
  line-height: 1;
  color: var(--color-text-muted);
  display: flex;
  align-items: center;
  justify-content: center;
}

.feedback-btn:hover {
  background: var(--color-bg-tertiary);
  border-color: var(--color-text-muted);
  color: var(--color-text-primary);
}

.thumb-down-btn svg {
  transform: rotate(180deg);
}

.feedback-icon {
  display: flex;
  align-items: center;
  color: var(--color-text-muted);
}

.feedback-icon.thumb-down svg {
  transform: rotate(180deg);
}

.feedback-thanks {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: var(--color-text-muted);
}

.feedback-submitted {
  opacity: 0.7;
}

.feedback-expanded {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.feedback-prompt {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: var(--color-text-secondary);
}

.feedback-textarea {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  padding: 12px;
  border: 1px solid #ccc;
  border-radius: 6px;
  background: white;
  color: var(--color-text-primary);
  resize: vertical;
  min-height: 72px;
}

.feedback-textarea::placeholder {
  color: var(--color-text-muted);
}

.feedback-textarea:focus {
  outline: none;
  border-color: var(--color-text-muted);
}

.feedback-actions {
  display: flex;
}

.feedback-submit {
  background: var(--color-text-primary);
  color: var(--color-bg-primary);
  border: none;
  border-radius: 6px;
  padding: 8px 20px;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition-fast);
}

.feedback-submit:hover {
  opacity: 0.85;
}

.feedback-submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
